import { useState, useEffect, useCallback } from "react";
import { process, aggregateBy } from "@progress/kendo-data-query";

import { getSelectedState } from "@progress/kendo-react-grid";
import { getter } from "@progress/kendo-react-common";

export function useClientGridDataState(
  mainGridData,
  dataItemKey,
  pageSize,
  aggregateColumnSettings,
  sendAggregatesParentCallback,
  selectionOptions = null
) {
  const singleSelect = !!(selectionOptions && selectionOptions.singleSelect);
  const initialSelectedKeys =
    (selectionOptions && selectionOptions.initialSelectedKeys) || [];
  const onSelectionChangedCallback =
    selectionOptions && selectionOptions.onSelectionChangedCallback;

  const INITIAL_MAIN_GRID_DATA_STATE = { skip: 0, take: pageSize };

  const [gridDataState, setGridDataState] = useState(
    INITIAL_MAIN_GRID_DATA_STATE
  );
  const [gridResultState, setGridResultState] = useState([]);

  const [selectedMainGridState, setSelectedMainGridState] = useState(() => {
    const initialState = {};
    initialSelectedKeys.forEach((key) => {
      initialState[key] = true;
    });
    return initialState;
  });

  const SELECTED_FIELD = "SelectedId";

  useEffect(() => {
    if (!onSelectionChangedCallback) return;
    onSelectionChangedCallback(
      Object.keys(selectedMainGridState).filter(
        (key) => selectedMainGridState[key] === true
      )
    );
  }, [selectedMainGridState]);

  //reset grid data state to initial state
  const ResetGridDataState = () => {
    setGridDataState(INITIAL_MAIN_GRID_DATA_STATE);
  };

  const onMainGridDataStateChange = (event) => {
    setGridDataState(event.dataState);
  };

  function GetExportData() {
    let filterSettings = {};
    filterSettings.filter = gridDataState.filter;
    filterSettings.sort = gridDataState.sort;

    let filterAndSortdata = process(mainGridData, filterSettings);
    return filterAndSortdata.data;

    //return mainGridResultState.data;
  }

  function setAggregates(currentDataState) {
    let aggregates = aggregateBy(
      currentDataState.data,
      aggregateColumnSettings
    );

    let returnValues = {};
    returnValues.data = aggregates;
    returnValues.filter = currentDataState.filter;

    sendAggregatesParentCallback(returnValues);
  }

  useEffect(() => {
    if (!mainGridData) {
      // Clear grid when no data is available (e.g., when query is disabled)
      setGridResultState({ data: [], total: 0 });
      return;
    }
    if (
      mainGridData &&
      aggregateColumnSettings &&
      sendAggregatesParentCallback
    ) {
      let filterSettings = {};
      filterSettings.filter = gridDataState.filter;

      let dataToAggregate = process(mainGridData, filterSettings);

      setAggregates(dataToAggregate);
    }

    if (mainGridData) {
      const idGetter = getter(dataItemKey);
      try {
        let processedData = process(
          mainGridData.map((item) => ({
            ...item,
            [SELECTED_FIELD]: selectedMainGridState[idGetter(item)],
          })),
          gridDataState
        );

        setGridResultState(processedData);
      } catch (error) {
        console.error("Error processing grid data:", error);
        setGridResultState({ data: [] });
      }
    }
  }, [mainGridData, gridDataState, selectedMainGridState]);

  const onSelectionChange = useCallback(
    (event) => {
      //alert("dataItemKey: " + dataItemKey);
      var columnIndex=0;
      if(isDataGrouped()) {
        columnIndex = gridDataState.group.length; // Adjust for grouped data
      }
      if (event.startColIndex === columnIndex) {
        try {
          const newSelectedState = getSelectedState({
            event,
            selectedState: selectedMainGridState,
            dataItemKey: dataItemKey,
          });

          if (singleSelect) {
            // Keep only the row that was just selected
            const newKeys = Object.keys(newSelectedState).filter(
              (key) =>
                newSelectedState[key] === true &&
                selectedMainGridState[key] !== true
            );
            Object.keys(newSelectedState).forEach((key) => {
              if (!newKeys.includes(key)) newSelectedState[key] = false;
            });
          }

          setSelectedMainGridState(newSelectedState);
        } catch (error) {
          console.error("Error in selection handling:", error);
          // If there's an error in selection, reset the selection state
          setSelectedMainGridState({});
        }
      }
    },
    [selectedMainGridState,gridDataState]
  );

  const GetSelectedGridDataKeys = () => {
    if (!selectedMainGridState) {
      return [];
    }

    var selectableKeys = Object.keys(selectedMainGridState);

    var selectedKeys = selectableKeys.filter(function (key) {
      return selectedMainGridState[key] === true;
    });

    return selectedKeys;
  };
  const GetSelectedGridData = () => {
    let selectedKeys = GetSelectedGridDataKeys();

    //NOTE: Get the main grid data where selectedKeys are equal to the dataItemKey
    if (!mainGridData || selectedKeys.length === 0) {
      return [];
    }

    // Filter the mainGridData to include only items where the dataItemKey value is in selectedKeys
    const selectedData = mainGridData.filter(item => {
      return selectedKeys.includes(String(item[dataItemKey]));
    });

    return selectedData;
  };

  const UnSelectAll = () => {
    //reset the selected state to nothing selected
    setSelectedMainGridState({});
  };

  const SelectAll = () => {
    if (singleSelect) return;
    // make sure every record in the VIEWABLE data is selected
    if (
      gridResultState &&
      gridResultState.data &&
      gridResultState.data.length > 0
    ) {
      let newSelectedState = {};
      const idGetter = getter(dataItemKey);
      
      if (isDataGrouped()) {
        // Handle grouped data
        const processGroupedItems = (data) => {
          data.forEach((item) => {
            if (item.items && Array.isArray(item.items)) {
              // This is a group, process its items recursively
              processGroupedItems(item.items);
            } else {
              // This is a regular data item, set it as selected
              newSelectedState[idGetter(item)] = true;
            }
          });
        };
        
        processGroupedItems(gridResultState.data);
      } else {
        // Handle flat data
        gridResultState.data.forEach((item) => {
          newSelectedState[idGetter(item)] = true;
        });
      } 
      setSelectedMainGridState(newSelectedState);
    }
  };

  // Helper function to check if data is grouped
  const isDataGrouped = () => {
    // Method 1: Check if gridDataState has group descriptors
    if (gridDataState && gridDataState.group && gridDataState.group.length > 0) {
      return true;
    }
    
    // Method 2: Check the structure of gridResultState data
    if (gridResultState && gridResultState.data && gridResultState.data.length > 0) {
      // In grouped data, first item will have 'items' property and 'field' property
      const firstItem = gridResultState.data[0];
      if (firstItem && firstItem.items && Array.isArray(firstItem.items) && firstItem.field) {
        return true;
      }
    }
    
    return false;
  }; 

  return {
    mainGridResultState: gridResultState,
    mainGridDataState: gridDataState,
    onMainGridDataStateChange,
    ResetGridDataState,
    onSelectionChange,
    SelectAll,
    UnSelectAll,
    GetSelectedGridDataKeys,
    GetSelectedGridData,
    GetExportData,
    isDataGrouped
  };
}
